import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import '../css/user/gridDropdown.css';
import ImpersonationModal from '../../../views/Modals/ReportModals/ImpersonationModal';
import ReportWhyModal from '../../../views/Modals/ReportModals/ReportWhyModal';

const MENU_CONTAINER_SELECTOR = '.creator-feed-menu-container';

// type="post" or type="creator"
const GridDropdown = ({
    className = '',
    iconClass = '',
    id = '',
    creatorId = '',
    isOpen = false,
    onToggle,
    isShowImpersonation = false,
    isSinglePost = false,
    postId = ''
}) => {
    const [isImpersonationModalOpen, setIsImpersonationModalOpen] = useState(false);
    const [isReportWhyModalOpen, setIsReportWhyModalOpen] = useState(false);

    // Toggle menu
    const toggleMenu = useCallback((e) => {
        e.stopPropagation();
        onToggle?.(id);
    }, [onToggle, id]);

    // Close menu
    const closeMenu = useCallback(() => {
        onToggle?.(null);
    }, [onToggle]);

    const onImpersonation = useCallback(() => {
        setIsImpersonationModalOpen(true);
    }, []);

    const closeImpersonationModal = useCallback(() => {
        setIsImpersonationModalOpen(false);
    }, []);

    const onReport = useCallback(() => {
        setIsReportWhyModalOpen(true);
    }, []);

    const closeReportWhyModal = useCallback(() => {
        setIsReportWhyModalOpen(false);
    }, []);

    // Handle menu actions
    const handleMenuAction = useCallback((action, e) => {
        e.stopPropagation();
        closeMenu();

        const actionMap = {
            impersonation: () => onImpersonation?.(),
            report: () => onReport?.(),
        };

        actionMap[action]?.();
    }, [closeMenu, onImpersonation, onReport]);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (!event.target.closest(MENU_CONTAINER_SELECTOR)) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, closeMenu]);

    return (
        <>
            <div
                className={`creator-feed-menu-container ${className}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="creator-feed-menu-trigger"
                    onClick={toggleMenu}
                    aria-label="Open menu"
                    aria-expanded={isOpen}
                >
                    <i className={`fa-solid ${iconClass}`} />
                </button>

                <div
                    className={`creator-feed-dropdown ${isOpen ? 'show' : ''}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {isShowImpersonation && (
                        <button
                            className="creator-feed-dropdown-item"
                            onClick={(e) => handleMenuAction('impersonation', e)}
                        >
                            <span className='icon_box'>
                                <i className="fa-solid fa-user-secret" />
                            </span>
                            <span>Impersonation</span>
                        </button>
                    )}
                    <button
                        className="creator-feed-dropdown-item"
                        onClick={(e) => handleMenuAction('report', e)}
                    >
                        <span className='icon_box'>
                            <i className="fa-solid fa-pen-to-square" />
                        </span>
                        <span>Report</span>
                    </button>
                </div>
            </div>

            <ImpersonationModal
                modalOpen={isImpersonationModalOpen}
                creatorId={id}
                onClose={closeImpersonationModal}
            />

            <ReportWhyModal
                modalOpen={isReportWhyModalOpen}
                creatorId={creatorId}
                isSinglePost={isSinglePost}
                postId={postId}
                onClose={closeReportWhyModal}
            />
        </>
    );
};

// PropTypes for type checking
GridDropdown.propTypes = {
    className: PropTypes.string,
    iconClass: PropTypes.string,
    type: PropTypes.string,
    id: PropTypes.string,
    creatorId: PropTypes.string,
    isOpen: PropTypes.bool,
    onToggle: PropTypes.func,
    isShowImpersonation: PropTypes.bool,
    isSinglePost: PropTypes.bool,
};

export default GridDropdown;

